import { useRef, useState } from "react";
import { trackMetaEvent } from "@/components/Analytics";

const STORAGE_KEY = "xpz_recently_viewed";
const MAX_ITEMS = 8;

const readStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeStorage = (items) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    // localStorage unavailable (private mode, quota, etc) — non-fatal, just log for visibility
    console.warn("useRecentlyViewed: failed to persist recently-viewed items", e);
  }
};

const recordView = (item) => {
  const existing = readStorage().filter((i) => i.path !== item.path);
  const updated = [{ ...item, viewedAt: Date.now() }, ...existing].slice(0, MAX_ITEMS);
  writeStorage(updated);
};

// Records the current product as viewed, and returns the other recently
// viewed items (excluding the current page) for rendering a strip.
// Recording + reading happens synchronously DURING render (guarded by a ref
// so it only fires once per distinct path) rather than in a useEffect —
// this avoids any effect-scheduling delay so the strip is correct on the
// very first paint, even during fast client-side navigation between pages.
export const useRecentlyViewed = (item) => {
  const recordedPathRef = useRef(null);
  const [items, setItems] = useState(() => readStorage().filter((i) => i.path !== item?.path));

  if (item && recordedPathRef.current !== item.path) {
    recordedPathRef.current = item.path;
    recordView(item);
    setItems(readStorage().filter((i) => i.path !== item.path));
    trackMetaEvent("ViewContent", {
      content_ids: [item.path],
      content_name: item.name,
      content_type: "product",
      currency: "NZD",
      value: item.price || item.priceFrom || 0,
    });
  }

  return items;
};
